﻿<script setup lang="ts">
    import { computed, inject, onMounted, reactive } from 'vue';
    import {
        getNextServiceLevelPrice,
        UpgradeLimitReference,
        UpgradeReference,
        upgradeAccount,
        AccountUpgradeReference,
    } from '@cwd/Account/AccountUpgrade/AccountUpgrade.access';
    import selectCreditCards from '@cwd/Account/CreditCards/SelectCreditCard.vue';
    import { CreditCardReference } from '@cwd/Account/CreditCards/CreditCards.access';
    import Loading from '@shared/components/Loading.vue';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { Permissions } from '@shared/Root/Permissions';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const props = defineProps<{
        currentServiceSubscription: any;
    }>();

    const state = reactive<{
        selectedLimit: number;
        slider: number;
        loading: boolean;
        upgradeReference: UpgradeReference | null;
        noUpgradeNeeded: boolean;
        noAvailableUpgrades: boolean;
        upgradingByInvoice: boolean;
        failedPayment: boolean;
        technicalError: boolean;
        termsAccepted: boolean;
        selectedCreditCard: CreditCardReference | null;
        upgradeLimitReference: UpgradeLimitReference | null;
        hasScope: boolean;
    }>({
        selectedLimit: 1000,
        slider: 1,
        loading: false,
        upgradeReference: null,
        noUpgradeNeeded: false,
        noAvailableUpgrades: false,
        upgradingByInvoice: false,
        failedPayment: false,
        technicalError: false,
        termsAccepted: false,
        selectedCreditCard: null,
        upgradeLimitReference: null,
        hasScope: false,
    });

    onMounted(async () => {
        state.loading = true;

        let urlParams = new URLSearchParams(window.location.search);
        var contactsLimit = urlParams.get('contactsLimit');
        var emailLimit = urlParams.get('emailLimit');
        var feature = urlParams.get('feature');
        var tier = urlParams.get('tier');

        state.upgradeLimitReference = {
            ContactsLimit: contactsLimit != null ? +contactsLimit : null,
            EmailLimit: emailLimit != null ? +emailLimit : null,
            Feature: feature,
            Tier: tier,
        };

        const requireScopeParams: vRequireScopeParams = {
            Feature: 'Account',
            Permission: Permissions.Write,
        };
        state.hasScope = scopeAccess.hasScope(requireScopeParams);

        await getNextServiceLevelPrice(state.upgradeLimitReference)
            .then((result) => {
                state.upgradeReference = result;

                if (!state.upgradeReference.CanUpgrade) {
                    if (state.upgradeReference.ReasonForNotUpgrading.indexOf('maximum') >= 0) state.noAvailableUpgrades = true;
                    else state.noUpgradeNeeded = true;
                }
            })
            .finally(() => (state.loading = false));

        if (props.currentServiceSubscription.DefaultPaymentMethod != 'CreditCard') state.upgradingByInvoice = true;
    });

    const showUpgradePart = computed(() => {
        return !state.noUpgradeNeeded && !state.noAvailableUpgrades && state.hasScope;
    });

    const showMoneyPart = computed(() => {
        if (props.currentServiceSubscription.Status == 1 || props.currentServiceSubscription.Status == 2) return true;

        return props.currentServiceSubscription.ServiceLevelPrice.ServiceLevelPriceId !== state.upgradeReference?.NextServiceLevelPrice.ServiceLevelPriceId;
    });

    const upgradeButtonEnabled = computed(() => {
        return (
            state.termsAccepted &&
            !state.upgradingByInvoice &&
            !state.noUpgradeNeeded &&
            state.selectedCreditCard != null &&
            contextAccess.state.currentAccount?.Status === 2
        );
    });

    async function upgrade(): Promise<void> {
        if (state.upgradeReference == null) return;
        if (state.selectedCreditCard == null) return;

        state.loading = true;

        let accountUpgradeReference: AccountUpgradeReference = {
            ServiceLevelPrice: state.upgradeReference.NextServiceLevelPrice,
            CreditCard: state.selectedCreditCard,
            SuccessfulPaymentUrl: '/Account/Upgraded',
            CancelledPaymentUrl: '/Account/CancelledPayment',
            InitiatedUrl: window.location.href,
            PayByInvoice: false,
            LimitInfo: state.upgradeLimitReference,
        };

        await upgradeAccount(accountUpgradeReference)
            .then((transactionId) => {
                document.location = '/Account/PaymentCompleted/' + transactionId;
            })
            .finally(() => (state.loading = false));
    }

    function creditCardSelected(selectedCreditCard: CreditCardReference): void {
        state.selectedCreditCard = selectedCreditCard;
    }

    const paidUntilString = (): string => {
        if (state.upgradeReference == null || state.upgradeReference.NextServiceLevelPrice.PaidUntil == null) return '';

        return $Resources.UpgradeAccountCurrentPlanPaidUntilLabel.replace(
            '{{PaidUntil}}',
            formatDate(new Date(state.upgradeReference.NextServiceLevelPrice.PaidUntil))
        ).replace('{{PaidDays}}', state.upgradeReference.NextServiceLevelPrice.DaysToGiveCreditFor.toString());
    };

    const paidUntilOverdueString = (): string => {
        if (state.upgradeReference == null || state.upgradeReference.NextServiceLevelPrice.PaidUntil == null) return '';

        return $Resources.UpgradeAccountCurrentPlanOverdueLabel.replace(
            '{{PaidUntil}}',
            formatDate(new Date(state.upgradeReference.NextServiceLevelPrice.PaidUntil))
        ).replace('{{PaidDays}}', state.upgradeReference.NextServiceLevelPrice.DaysToPayOldLevel.toString());
    };

    const upgradeChargeString = (): string => {
        if (state.upgradeReference == null) return '';

        return $Resources.UpgradeAccountCurrentPlanUpgradeChargeLabel.replace(
            '{{ServiceLevel}}',
            state.upgradeReference.NextServiceLevelPrice.ServiceSubscriptionTier.Name + ' ' + state.upgradeReference.NextServiceLevelPrice.ServiceLevel.Name
        ).replace('{{DaysToPayNewLevel}}', state.upgradeReference.NextServiceLevelPrice.DaysToPayNewLevel.toString());
    };

    const oneTimeChargeString = (): string => {
        if (state.upgradeReference == null || state.upgradeReference.NextServiceLevelPrice == null) return '';

        return $Resources.UpgradeAccountAcceptOneTimeChargeMessage.replace(
            '{{TotalDue}}',
            state.upgradeReference.NextServiceLevelPrice.TotalDue.toFixed(2)
        ).replace('{{CurrencyCode}}', state.upgradeReference.NextServiceLevelPrice.Currency.CurrencyCode.toString());
    };

    const recurringChargeString = (): string => {
        if (state.upgradeReference == null || state.upgradeReference.NextServiceLevelPrice == null) return '';

        return $Resources.UpgradeAccountAcceptRecurringChargeMessage.replace(
            '{{NextPaymentDate}}',
            formatDate(new Date(state.upgradeReference.NextServiceLevelPrice.NextPaymentDate))
        )
            .replace('{{NextPaymentAmount}}', state.upgradeReference.NextServiceLevelPrice.NextPaymentAmount.toFixed(2))
            .replace('{{CurrencyCode}}', state.upgradeReference.NextServiceLevelPrice.Currency.CurrencyCode.toString());
    };

    function formatMoney(currency: string, number: number): string {
        let numberInCurrency = new Intl.NumberFormat(navigator.language, { style: 'currency', currency: currency, maximumFractionDigits: 2 }).format(number);

        return numberInCurrency;
    }

    function formatDate(date: Date): string {
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };

        return date.toLocaleDateString(navigator.language, options);
    }
</script>

<template>
    <div class="text-center">
        <svg
            class="ug-logo-inline d-inline-block img-responsive"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            version="1.1"
            id="Layer_1"
            x="0px"
            y="0px"
            viewBox="0 0 602 130.7"
            style="enable-background: new 0 0 602 130.7"
            xml:space="preserve">
            <g>
                <path
                    class="st0"
                    d="M204.6,91.8h-9.9l-0.1-7.3c-2.9,5.1-8.8,8.5-15.7,8.5c-6.3,0-11-1.9-14.1-5.9c-2.5-3.3-3.7-8.6-3.7-15.7V40.9   h10.2V71c0,8.2,2.4,13.5,10.6,13.5c8.5,0,12.6-7,12.6-16.3V40.9h9.9l0.1,7.3c2.9-5.1,8.8-8.5,15.7-8.5c6.3,0,11,1.9,14.1,5.9   c2.5,3.3,3.7,8.6,3.7,15.7v30.5h-10.2v-30c0-8.2-2.4-13.5-10.6-13.5c-8.4,0-12.6,7-12.6,16.3V91.8L204.6,91.8z" />
                <path
                    class="st0"
                    d="M300.9,41v40.1c0,10.2-1.8,17.5-5.6,21.9c-5.2,6.5-12.8,9.7-22.7,9.7c-8,0-15.6-2.6-22.7-7.6l4.8-7.2   c1.6,1.4,4.2,2.9,7.7,4.3s7.1,2.1,10.5,2.1c6.3,0,11-1.8,13.9-5.6c2.6-3.1,3.9-7.9,3.9-14.2v-2.6h-0.2c-4.1,6.1-9.9,9.1-17.8,9.1   c-6.8,0-12.6-2.4-17.4-7c-4.7-4.6-7.1-10.8-7.1-18.6s2.4-14,7.1-18.6c4.8-4.6,10.6-7,17.4-7c8.2,0,14.4,3.7,17.8,9h0.3l-0.1-8h10.2   V41z M258.7,65.6c0,9.9,6,17.2,16.2,17.2S291,75.5,291,65.6s-6-17.3-16.1-17.3C264.9,48.3,258.7,55.7,258.7,65.6" />
                <path
                    class="st0"
                    d="M354.5,91.8h-9.9l-0.1-6.4c-4.8,5-10.2,7.6-16.1,7.6c-4.9,0-9.1-1.4-12.5-4.3c-3.3-2.9-5-6.6-5-11.4   c0-9.7,7.6-15,19.9-15.5l13.6-0.5v-2c0-7.4-4.9-11.6-12.6-11.6c-5.5,0-10.6,1.5-15.3,4.4l-3.1-6.8c6-3.4,12.3-5.1,19-5.1   c7.4,0,12.9,1.9,16.8,5.8c3.6,3.7,5.5,9.3,5.5,16.5L354.5,91.8L354.5,91.8z M344.4,67.5l-11.9,0.8c-7.7,0.4-11.6,3.3-11.6,8.8   c0,5,4,8.8,10.5,8.8c5.3,0,9.6-2.5,12.9-7.4v-11H344.4z" />
                <path
                    class="st0"
                    d="M413.9,85.5c-4.5,5-10.5,7.5-17.9,7.5c-7.8,0-13.6-3-17.5-9.1l-0.1-0.1v27.9h-10.3V41h9.8v8h0.2   c4.5-6,10.5-9,18.1-9c7.4,0,13.3,2.5,17.8,7.5c4.5,4.9,6.7,11.2,6.7,18.8C420.8,74.1,418.5,80.5,413.9,85.5 M410.3,66.3   c0-5.2-1.5-9.5-4.4-12.8s-6.7-5-11.8-5c-5,0-9,1.7-11.9,5s-4.3,7.6-4.3,12.8s1.4,9.5,4.3,12.9c2.9,3.3,6.8,5,11.9,5   c5,0,8.9-1.7,11.8-5C408.8,75.8,410.3,71.5,410.3,66.3" />
                <path
                    class="st0"
                    d="M477.7,85.5c-4.5,5-10.5,7.5-17.9,7.5c-7.8,0-13.6-3-17.5-9.1l-0.1-0.1v27.9h-10.3V41h9.8v8h0.2   c4.5-6,10.5-9,18.1-9c7.4,0,13.3,2.5,17.8,7.5c4.5,4.9,6.7,11.2,6.7,18.8C484.5,74.1,482.3,80.5,477.7,85.5 M474,66.3   c0-5.2-1.5-9.5-4.4-12.8s-6.7-5-11.8-5s-9,1.7-11.9,5s-4.3,7.6-4.3,12.8s1.4,9.5,4.3,12.9c2.9,3.3,6.8,5,11.9,5c5,0,8.9-1.7,11.8-5   C472.5,75.8,474,71.5,474,66.3" />
                <path
                    class="st0"
                    d="M541.3,68.9h-39.1c0.5,9.9,7,16.1,16.4,16.1c9.5,0.1,15.6-5.2,16.8-5.9l4.3,5.9l-0.4,0.4   c-1.7,1.7-8.5,7.6-21.9,7.6c-7.6,0-13.7-2.5-18.4-7.3c-4.7-4.8-7.1-11.3-7.1-19.4c0-7.8,2.5-14.2,7.4-19c5-4.8,11.1-7.3,18.4-7.3   c7.1,0,12.8,2.2,17.3,6.8c4.5,4.5,6.7,10.3,6.7,17.4L541.3,68.9z M531.9,62c-0.1-3.4-1.4-6.7-4-9.8c-2.5-3.1-6.1-4.7-10.7-4.7   s-8.2,1.5-10.8,4.6c-2.6,3-4,6.3-4.2,9.9L531.9,62L531.9,62z" />
                <path
                    class="st0"
                    d="M601,18v73.8h-9.8v-8.1H591c-4.2,6.2-10.1,9.3-17.9,9.3c-7.4,0-13.4-2.5-18-7.5c-4.5-5-6.7-11.4-6.7-19.1   c0-7.6,2.2-13.9,6.7-18.8c4.5-5,10.4-7.5,17.8-7.5c7.8,0,13.8,3,17.8,8.9h0.2V18H601L601,18z M591.2,66.3c0-5.2-1.5-9.5-4.4-12.8   s-6.7-5-11.8-5c-5,0-9,1.7-11.9,5s-4.3,7.6-4.3,12.8s1.4,9.5,4.3,12.9c2.9,3.3,6.8,5,11.9,5c5,0,8.9-1.7,11.8-5   C589.7,75.8,591.2,71.5,591.2,66.3" />
                <path
                    class="st1"
                    d="M98.4,53.8V64c0,17-13.8,30.7-30.7,30.7S37,80.9,37,64V28.2c0-4.2-3.4-7.7-7.7-7.7c-4.2,0-7.7,3.4-7.7,7.7   v35.9c0,25.4,20.7,46.1,46.1,46.1s46.1-20.7,46.1-46.1V28.2c0-4.2-3.4-7.7-7.7-7.7c-4.2,0-7.7,3.4-7.7,7.7v10   c0,1.4-1.1,2.6-2.6,2.6s-2.6-1.1-2.6-2.6v-10c0-7.1,5.8-12.9,12.8-12.9c7.1,0,12.8,5.7,12.8,12.8V64c0,28.2-23,51.2-51.2,51.2   S16.4,92.3,16.4,64V28.2c0-7.1,5.7-12.8,12.8-12.8c7-0.1,12.8,5.7,12.8,12.8v35.9c0,14.1,11.5,25.6,25.6,25.6s25.6-11.5,25.6-25.6   V59c0-4.2-3.4-7.7-7.7-7.7c-4.2,0-7.7,3.4-7.7,7.7v5.1c0,5.6-4.6,10.2-10.3,10.2s-10.2-4.6-10.2-10.2V28.2C57.3,12.7,44.7,0,29.1,0   C13.6,0,1,12.6,1,28.2v35.9c0,36.7,29.9,66.6,66.6,66.6s66.6-29.9,66.6-66.6V28.2C134.2,12.7,121.6,0,106,0   C90.5,0,77.8,12.6,77.8,28.2v15.1c0,1.4,1.1,2.6,2.6,2.6c1.4,0,2.6-1.1,2.6-2.6V28.2c0-12.8,10.3-23.1,23-23.1s23.1,10.3,23.1,23.1   v35.9c0,33.9-27.6,61.5-61.5,61.5S6.1,98,6.1,64.1V28.2c0-12.7,10.3-23.1,23.1-23.1c12.7,0,23.1,10.3,23.1,23.1v35.9   c0,8.5,6.9,15.4,15.4,15.4S83,72.5,83,64v-5.1c0-1.4,1.1-2.6,2.6-2.6c1.4,0,2.6,1.1,2.6,2.6V64c0,11.3-9.2,20.5-20.5,20.5   S47.2,75.3,47.2,64V28.2c0-9.9-8-17.9-17.9-17.9s-17.9,8-17.9,17.9v35.9c0,31.1,25.3,56.4,56.4,56.4s56.4-25.3,56.4-56.4V28.2   c0-9.9-8-17.9-17.9-17.9s-17.9,8-17.9,17.9v10c0,4.2,3.4,7.7,7.7,7.7c4.2,0,7.7-3.4,7.7-7.7v-10c0-1.4,1.1-2.6,2.6-2.6   c1.4,0,2.6,1.1,2.6,2.6v35.9c0,22.6-18.4,41-41,41s-41-18.4-41-41V28.2c0-1.4,1.1-2.6,2.6-2.6c1.4,0,2.6,1.1,2.6,2.6v35.9   c0,19.8,16.1,35.9,35.9,35.9S104,83.9,104,64.1V53.8c0-1.4-1.1-2.6-2.6-2.6C99.5,51.2,98.4,52.4,98.4,53.8" />
            </g>
        </svg>
    </div>
    <h1 class="h2 text-center m-xl-t m-xl-b">{{ $Resources.UpgradeAccountHeading }}</h1>

    <Loading :show="state.loading"></Loading>

    <div v-if="!state.loading">
        <div class="alert alert-info alert-fixed-i m-0-b" v-if="!state.hasScope">
            <p>
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <span v-html="$Resources.OnlyAdminsCanUpgradeMessage"></span>
            </p>
            <div class="form-group">
                <a href="/Dashboard" class="btn btn-info btn-sm">{{ $Resources.BackToDashboardBtn }}</a>
            </div>
        </div>

        <div class="alert alert-info alert-fixed-i m-0-b" v-if="state.noAvailableUpgrades">
            <p>
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <span v-html="$Resources.NoAvailableUpgradesMessage"></span>
            </p>
            <div class="form-group">
                <a href="/Dashboard" class="btn btn-info btn-sm">{{ $Resources.BackToDashboardBtn }}</a>
            </div>
        </div>

        <div class="alert alert-info alert-fixed-i" v-if="state.noUpgradeNeeded">
            <p>
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <span v-html="$Resources.NoNeedToUpgradeMessage"></span>
            </p>
            <div class="form-group">
                <a href="/Dashboard" class="btn btn-info btn-sm">{{ $Resources.BackToDashboardBtn }}</a>
            </div>
        </div>

        <div v-if="showUpgradePart && state.upgradeReference">
            <div class="d-flex flex-wrap gap-lg flex-even text-center">
                <div class="hidden-xs" v-if="contextAccess.state.currentAccount?.ServiceSubscriptionStatus !== 1">
                    <p class="c-light">{{ $Resources.UpgradeAccountCurrentSubscriptionLabel }}</p>
                    <div class="panel panel-default">
                        <div class="panel-heading">
                            <h3 class="panel-title">
                                {{ props.currentServiceSubscription.ServiceLevelPrice.ServiceSubscriptionTier.Name }}
                                {{ props.currentServiceSubscription.ServiceLevelPrice.ServiceLevel.Name }}
                            </h3>
                        </div>
                        <div class="panel-body p-sm-t p-sm-b">
                            <p class="h1 m-0-t m-xs-b">{{ props.currentServiceSubscription.MonthlyPrice }}</p>
                            <p class="meta">{{ props.currentServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode }}/{{ $Resources.PerMonthLabel }}</p>
                        </div>
                    </div>
                </div>
                <div>
                    <p class="c-light">{{ $Resources.UpgradeAccountNewSubscriptionLabel }}</p>
                    <div class="panel panel-primary">
                        <div class="panel-heading">
                            <h3 class="panel-title">
                                {{ state.upgradeReference.NextServiceLevelPrice.ServiceSubscriptionTier.Name }}
                                {{ state.upgradeReference.NextServiceLevelPrice.ServiceLevel.Name }}
                            </h3>
                        </div>
                        <div class="panel-body p-sm-t p-sm-b">
                            <p class="h1 m-0-t m-xs-b">{{ state.upgradeReference.NextServiceLevelPrice.MonthlyPrice }}</p>
                            <p class="meta">{{ state.upgradeReference.NextServiceLevelPrice.Currency.CurrencyCode }}/{{ $Resources.PerMonthLabel }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="showMoneyPart">
                <div>
                    <div class="table-responsive m-0-t m-lg-b">
                        <ul class="list text-lg fw-600 m-0-all text-nowrap b-0-t">
                            <li class="list-row">
                                <div class="list-cell">
                                    <span>
                                        {{ state.upgradeReference.NextServiceLevelPrice.ServiceSubscriptionTier.Name }}
                                        {{ state.upgradeReference.NextServiceLevelPrice.ServiceLevel.Name }}
                                    </span>
                                </div>
                                <div class="list-cell text-right"></div>
                            </li>
                            <li class="list-row" v-if="state.upgradeReference.NextServiceLevelPrice.DaysToPayOldLevel > 0">
                                <div class="list-cell text-md p-sm-t p-sm-b normal">
                                    <span>
                                        {{ paidUntilOverdueString() }}
                                    </span>
                                </div>
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                    <span>
                                        {{
                                            formatMoney(
                                                state.upgradeReference.NextServiceLevelPrice.Currency.CurrencyCode,
                                                state.upgradeReference.NextServiceLevelPrice.OverDraftAmount
                                            )
                                        }}
                                    </span>
                                </div>
                            </li>

                            <li class="list-row" v-if="state.upgradeReference.NextServiceLevelPrice.DaysToPayNewLevel > 0">
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-nowrap">
                                    <span>
                                        {{ upgradeChargeString() }}
                                    </span>
                                </div>
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                    <span>
                                        {{
                                            formatMoney(
                                                state.upgradeReference.NextServiceLevelPrice.Currency.CurrencyCode,
                                                state.upgradeReference.NextServiceLevelPrice.UpgradeAmount
                                            )
                                        }}
                                    </span>
                                </div>
                            </li>

                            <li class="list-row" v-if="state.upgradeReference.NextServiceLevelPrice.DaysToGiveCreditFor > 0">
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-nowrap">
                                    <span>
                                        {{ paidUntilString() }}
                                    </span>
                                </div>
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                    <span>
                                        -{{
                                            formatMoney(
                                                state.upgradeReference.NextServiceLevelPrice.Currency.CurrencyCode,
                                                state.upgradeReference.NextServiceLevelPrice.CreditAmount
                                            )
                                        }}
                                    </span>
                                </div>
                            </li>

                            <li class="list-row" v-if="state.upgradeReference.NextServiceLevelPrice.VatAmount > 0">
                                <div class="list-cell text-md p-sm-t p-sm-b normal">
                                    {{ $Resources.VatLabel }}
                                </div>
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                    <span>
                                        {{
                                            formatMoney(
                                                state.upgradeReference.NextServiceLevelPrice.Currency.CurrencyCode,
                                                state.upgradeReference.NextServiceLevelPrice.VatAmount
                                            )
                                        }}
                                    </span>
                                </div>
                            </li>
                            <li class="list-row list-row-summary p-relative">
                                <div class="list-cell b-0-b">
                                    {{ $Resources.TotalDueLabel }}
                                </div>
                                <div class="list-cell b-0-b text-right">
                                    <span>
                                        {{
                                            formatMoney(
                                                state.upgradeReference.NextServiceLevelPrice.Currency.CurrencyCode,
                                                state.upgradeReference.NextServiceLevelPrice.TotalDue
                                            )
                                        }}
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <!-- <div class="form-group"
						data-bind="visible: $root.CurrentAccount() && TotalDue <= $root.CurrentServiceSubscription().InvoiceLimit">
						<div class="checkbox">
							<label>
								<input type="checkbox"
									data-bind="checked: $root.AccountUpgrade().PayByInvoice" />
								{{ $Resources.PayByInvoiceLabel
							</label>
						</div>
					</div> -->

                    <div>
                        <div v-if="props.currentServiceSubscription.DefaultPaymentMethod == 'CreditCard'">
                            <selectCreditCards
                                @creditCardSelected="creditCardSelected"
                                :selectedCreditCardId="state.selectedCreditCard?.CreditCardId"
                                :currentAccount="contextAccess.state.currentAccount"></selectCreditCards>

                            <ul class="list-checklist list-unstyled">
                                <li>
                                    <span v-html="oneTimeChargeString()"></span>
                                </li>
                                <li>
                                    <span v-html="recurringChargeString()"></span>
                                </li>
                            </ul>
                        </div>

                        <div class="alert alert-danger alert-fixed-i" v-if="state.upgradingByInvoice">
                            <i class="fa fa-ban" aria-hidden="true"></i>
                            <span v-html="$Resources.UpgradingByInvoiceMessage"></span>
                        </div>
                    </div>
                </div>

                <div class="alert alert-danger alert-fixed-i" v-if="state.failedPayment">
                    <i class="fa fa-ban" aria-hidden="true"></i>
                    <span v-html="$Resources.AutoPaymentFailedMessage"></span>
                </div>

                <div class="alert alert-danger alert-fixed-i" v-if="state.technicalError">
                    <i class="fa fa-ban" aria-hidden="true"></i>
                    <span v-html="$Resources.PaymentFailedDueToTechnicalErrorMessage"></span>
                </div>

                <div class="form-group">
                    <div class="checkbox slide-toggle">
                        <label class="label-lg">
                            <input type="checkbox" v-model="state.termsAccepted" />
                            <span class="toggle"></span>
                            <span v-html="$Resources.UpgradeAccountAcceptTermsLabel"></span>
                        </label>
                    </div>
                </div>

                <div class="form-group">
                    <button class="btn btn-cta btn-lg btn-block" @click="upgrade()" :disabled="!upgradeButtonEnabled">
                        {{ $Resources.UpgradeAccountPayAndUpgradeBtn }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="less">
    .st0 {
        fill: #1f1c26;
    }
    .st1 {
        fill: #1fa9b0;
    }
</style>
